import React from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CityData, Option } from '@/interfaces';
import { countriesService, GET_STATES } from '@/api';
import { useDebounce } from 'use-debounce';
import {
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';
import { InputList } from '@/components/form';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';

interface Props {
	register: UseFormRegister<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	cityId: number | undefined;
	cityName: string | undefined;
	stateId: number | undefined;
	setValue: UseFormSetValue<FieldValues>;
	cityError: any;
	isFieldsRequired: boolean;
}

export const SearchCities: React.FC<Props> = ({
	cityId,
	cityName,
	cityError,
	stateId,
	setValue,
	register,
	watch,
	isFieldsRequired,
}) => {
	const { toastError } = useNotification();

	const scrollExecuted = React.useRef(false);
	const [scrollTarget, setScrollTarget] = React.useState<string | null>(null);

	const [state, setState] = React.useState<string>('');
	const [allStates, setAllStates] = React.useState<Option[]>();

	const [searchTermCity, setSearchTermCity] = React.useState<string>('');
	const [citySelected, setCitySelected] = React.useState<Option>();

	const [defaultCityOption, setDefaultCityOption] =
		React.useState<Option | null>(null);

	const [searchFilter] = useDebounce(searchTermCity, 500);

	const { data: statesData } = useQuery({
		queryKey: [GET_STATES],
		queryFn: () => countriesService.getStates('1'),
	});

	const { data: defaultCityData } = useQuery({
		queryKey: ['GET_CITY_BY_ID', cityId],
		queryFn: () => countriesService.getCityById(String(cityId) ?? ''),
		enabled: !!cityId,
	});

	const {
		data: citiesData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading: isCitiesQueryLoading,
		refetch,
	} = useInfiniteQuery<CityData>({
		queryKey: ['GET_CITIES_BY_STATE', state, searchFilter],
		queryFn: async ({ pageParam = 1 }) => {
			const response = await countriesService.getCities(
				state,
				pageParam as number,
				searchFilter,
				100
			);
			return response.data;
		},
		getNextPageParam: (lastPage) => {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		initialPageParam: 1,
		enabled: !!state,
	});

	const allCities = React.useMemo(() => {
		if (!citiesData?.pages) return [];

		const auxCities = citiesData.pages.flatMap((page) =>
			page.cities.map((city) => ({
				label: city.name,
				name: `${city.id}`,
				placeholder: false,
			}))
		);

		if (stateId && String(stateId) !== state) {
			auxCities.unshift({
				name: '',
				label: 'Ex: Los Angeles',
				placeholder: true,
			});
		}

		return auxCities;
	}, [citiesData, stateId, state]);

	const observer = React.useRef<IntersectionObserver | null>(null);
	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | HTMLLIElement | null) => {
			if (isFetchingNextPage) return;

			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});

			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage, fetchNextPage]
	);

	React.useEffect(() => {
		if (statesData) {
			const auxStates: Option[] = [
				{
					name: '',
					label: 'Ex: California',
					placeholder: true,
				},
			];
			statesData.data.states.map((state: { id: number; name: string }) =>
				auxStates.push({
					label: state.name,
					name: `${state.id}`,
				})
			);
			setAllStates(auxStates);
		}
	}, [statesData]);

	React.useEffect(() => {
		if (defaultCityData?.data?.city && !defaultCityOption) {
			const city = defaultCityData.data.city;
			const option = {
				label: city.name,
				name: String(city.id),
				placeholder: false,
			};
			setTimeout(() => {
				setDefaultCityOption(option);
				setCitySelected(option);
			}, 200);
			setValue('city', city.id, { shouldValidate: true });
			setValue('cityName', city.name, { shouldValidate: true });
		}
	}, [defaultCityData]);

	React.useEffect(() => {
		if (!scrollTarget || !allCities || scrollExecuted.current) return;
		const cityOption = allCities.find((c) => c.name === scrollTarget);
		if (!cityOption) return;

		const element = document.querySelector(`[data-city-id="${scrollTarget}"]`);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			scrollExecuted.current = true;

			setTimeout(() => setScrollTarget(null), 1000);
			setValue('city', String(cityOption.name), { shouldValidate: true });
			setDefaultCityOption(cityOption);
			setCitySelected(cityOption);
		}
	}, [allCities, scrollTarget]);

	const handleCity = async (
		cityName: string,
		callback: any,
		cityId: string | number | undefined
	) => {
		try {
			const stateId = watch('state');
			if (!stateId) return;

			let id = cityId;
			if (cityId) {
				const response = await countriesService.updateCity(Number(cityId), {
					name: cityName,
					stateId: Number(stateId),
				});
				id = response.data.id;
				setScrollTarget(String(id));
			} else {
				const response = await countriesService.createCity({
					name: cityName,
					stateId: Number(stateId),
				});
				id = response.data.id;
				setScrollTarget(String(id));
			}

			const initials = cityName.slice(0, 2);
			setValue('city', String(id), { shouldValidate: true });
			setValue('cityName', cityName, { shouldValidate: true });
			setSearchTermCity(initials.toLocaleLowerCase());
			scrollExecuted.current = false;
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			callback();
		}
	};

	const handleDeleteCity = async (cityId: string) => {
		try {
			const city = citiesData?.pages
				.flatMap((page) => page.cities)
				.find((city) => city.id === Number(cityId));

			if (city) {
				await countriesService.deleteCity(Number(city.id));
				if (cityId && cityName) {
					setScrollTarget(String(cityId));
					const search = cityName.length > 3 ? cityName.slice(0, 3) : cityName;
					setSearchTermCity(search);
					scrollExecuted.current = false;
				} else {
					refetch();
				}
			}
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const handleCitySelectionChange = (selectedCityId: string) => {
		// selectedCityId is the 'name' property of the Option, which is the city.id as a string
		let newSelectedOption: Option | undefined | null = null;

		if (allCities) {
			newSelectedOption = allCities.find((opt) => opt.name === selectedCityId);
		}

		if (
			!newSelectedOption &&
			defaultCityOption &&
			defaultCityOption.name === selectedCityId
		) {
			newSelectedOption = defaultCityOption;
		}

		if (newSelectedOption) {
			setCitySelected(newSelectedOption);
			const label =
				typeof newSelectedOption.label === 'string'
					? newSelectedOption.label
					: '';
			setValue('cityName', label, { shouldValidate: true });
		} else {
			// This case might occur if the selected city is not in the current 'allCities' list
			// (e.g., after a new city creation and before allCities is fully updated).
			// For now, we log a warning. If this is a common scenario, robust handling would be needed.
			console.warn(
				`Selected city ID ${selectedCityId} not found in current options or defaultCityOption.`
			);
		}
	};

	return (
		<>
			{allStates && (
				<InputList
					myDefaultValue={stateId ? String(stateId) : undefined}
					register={register}
					name="state"
					title="State"
					isSearch
					options={allStates}
					setValueInput={setValue}
					handleChange={(value) => {
						setState(value);
						setValue('city', '');
						setValue('cityName', '');
						const option = allStates?.find((s) => s.name === value);
						setValue(
							'stateName',
							typeof option?.label === 'string' ? option.label : '',
							{ shouldValidate: true }
						);
					}}
					maxHeight="lg:max-w-[402px] bottom-full mb-1 z-[99]"
					markAsRequired={isFieldsRequired}
				/>
			)}
			{(allCities || citySelected) && (
				<InputList
					from="city"
					myDefaultValue={citySelected?.name}
					myDefaultValueOption={citySelected}
					name="city"
					title="City"
					register={register}
					options={allCities || []}
					setValueInput={setValue}
					className="w-full"
					disabled={!state}
					loading={isCitiesQueryLoading}
					resetValue={!watch('city')}
					maxHeight="bottom-full mb-1"
					isSearch
					showActionButtons
					setInputCreate={handleCity}
					setInputDelete={handleDeleteCity}
					containerRef={lastItemCallback}
					handleSearch={(value) => setSearchTermCity(value)}
					handleChange={handleCitySelectionChange}
					error={cityError}
				/>
			)}
		</>
	);
};
